import type { GamificationState } from "../store/gamification-store";
import { ACHIEVEMENTS } from "../achievements";
import { Modal } from "../../../ui/molecules/Modal";
import { TrophyIcon } from "../../../ui/icons";

interface AchievementsPanelProps {
  readonly state: GamificationState;
  readonly onClose: () => void;
}

export function AchievementsPanel({
  state,
  onClose,
}: AchievementsPanelProps) {
  const achievementSet = new Set(state.achievements);

  return (
    <Modal
      title="Accomplissements"
      description={`${state.achievements.length}/${ACHIEVEMENTS.length} débloqués`}
      onClose={onClose}
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {ACHIEVEMENTS.map((achievement) => {
          const isUnlocked = achievementSet.has(achievement.id);
          const progress = achievement.progress?.(state);
          const progressPct = progress
            ? Math.min(Math.round((progress.current / progress.target) * 100), 100)
            : null;

          return (
            <div
              key={achievement.id}
              className={`p-4 rounded-lg border transition-all ${
                isUnlocked
                  ? "bg-accent/10 border-accent/40 shadow-md"
                  : "bg-surface-alt border-border opacity-70"
              }`}
            >
              <div className="flex flex-col items-center gap-2.5">
                {/* Icon */}
                <div
                  className={`w-14 h-14 flex items-center justify-center rounded-xl ${
                    isUnlocked
                      ? "bg-accent/20"
                      : "bg-surface grayscale opacity-50"
                  }`}
                >
                  <achievement.Icon className={`w-6 h-6 ${isUnlocked ? "text-accent" : "text-ink-muted"}`} />
                </div>

                {/* Text */}
                <div className="text-center">
                  <p className={`text-xs font-semibold ${isUnlocked ? "text-ink" : "text-ink-muted"}`}>
                    {achievement.title}
                  </p>
                  <p className="text-[11px] text-ink-faint mt-0.5 line-clamp-2">
                    {achievement.description}
                  </p>
                </div>

                {/* Progress bar (locked only) */}
                {progress && !isUnlocked && (
                  <div className="w-full flex flex-col gap-1">
                    <div className="w-full h-1.5 bg-border rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent/60 rounded-full transition-all duration-300"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-ink-faint text-center">
                      {progress.current}/{progress.target}
                    </span>
                  </div>
                )}

                {/* Status */}
                {isUnlocked && (
                  <span className="text-[10px] font-medium text-accent flex items-center gap-1">
                    <TrophyIcon className="w-3 h-3" />
                    Débloqué
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
